const ACTUAL_RENT_LABEL = 'Actual Rent'

export const HISTORICAL_CHART_BASE_OPTIONS = {
  legend: { display: false },
  scales: { yAxes: [{ id: 'y', position: 'right' }] },
  animation: { duration: 0 },
  hover: {
    mode: 'index',
    intersect: false,
    animationDuration: 0
  },
  elements: {
    line: { tension: 0.1 },
    point: { hitRadius: 8, hoverRadius: 5 }
  },
  tooltips: { enabled: false }
}

export function getMouseY(event, chart) {
  const native = event && (event.native || event)
  if (!native || !chart || !chart.canvas) return null
  const rect = chart.canvas.getBoundingClientRect()
  if (typeof native.clientY === 'number') {
    return native.clientY - rect.top
  }
  if (typeof native.offsetY === 'number') return native.offsetY
  return null
}

export function filterValidActiveElements(active, chart) {
  if (!active || !chart || !chart.data || !chart.data.datasets) return []
  return active.filter((el) => {
    const dataset = chart.data.datasets[el._datasetIndex]
    if (!dataset) return false
    const value = dataset.data && dataset.data[el._index]
    if (value == null || value === '') return false
    const y = el._model && el._model.y
    return y != null && Number.isFinite(y)
  })
}

export function pickNearestElement(active, mouseY) {
  if (!active || active.length === 0) return null
  if (mouseY == null) return active[0]

  let nearest = active[0]
  let minDist = Infinity
  active.forEach((el) => {
    const y = el._model && el._model.y
    if (y == null) return
    const dist = Math.abs(y - mouseY)
    if (dist < minDist) {
      minDist = dist
      nearest = el
    }
  })
  return nearest
}

export function getElementPosition(element) {
  if (!element) return null
  if (typeof element.tooltipPosition === 'function') {
    return element.tooltipPosition()
  }
  const model = element._model
  if (!model || model.x == null || model.y == null) return null
  return { x: model.x, y: model.y }
}

export function formatLineValue(value) {
  if (value == null || value === '') return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

export function handleHistoricalChartHover(vm, event, active = []) {
  try {
    if (!active || active.length === 0) {
      vm.clearHistoricalChartHoverState()
      return
    }

    const anyEl = active[0]
    const chart = anyEl && anyEl._chart ? anyEl._chart : null
    if (!chart || !chart.data) {
      vm.clearHistoricalChartHoverState()
      return
    }

    const mouseY = getMouseY(event, chart)
    const validActive = filterValidActiveElements(active, chart)
    if (validActive.length === 0) {
      vm.clearHistoricalChartHoverState()
      return
    }
    const hoveredEl = pickNearestElement(validActive, mouseY)

    const datasetIndex = hoveredEl._datasetIndex
    const index = hoveredEl._index
    const dataset = chart.data.datasets && chart.data.datasets[datasetIndex]

    const label = chart.data.labels && chart.data.labels[index]
    const pos = getElementPosition(hoveredEl)
    if (!pos || pos.x == null || pos.y == null) {
      vm.clearHistoricalChartHoverState()
      return
    }

    const rect = chart.canvas.getBoundingClientRect()
    const left = rect.left + pos.x
    const top = rect.top + pos.y
    const centerLeft = rect.left + rect.width / 2
    const hoverKey = `${datasetIndex}:${index}`

    if (dataset.label === ACTUAL_RENT_LABEL) {
      const t = vm.toTimeKey(label)
      const item = t != null ? vm.actualRentByTime.get(t) : null
      if (!item) {
        vm.clearHistoricalChartHoverState()
        return
      }
      vm.showHistoricalChartTooltip({
        hoverKey,
        mode: 'actual-rent',
        left,
        top,
        centerLeft,
        item
      })
      return
    }

    const lineValue = formatLineValue(dataset.data && dataset.data[index])

    vm.showHistoricalChartTooltip({
      hoverKey,
      mode: 'line',
      left,
      top,
      centerLeft,
      lineLabel: dataset.label || '',
      lineValue,
      dateLabel: label || ''
    })
  } catch (_e) {
    vm.clearHistoricalChartHoverState()
  }
}

export const historicalChartHoverMixin = {
  data() {
    return {
      actualRentByTime: new Map(),
      _historicalChartHoverKey: '',
      historicalChartTooltip: {
        visible: false,
        mode: 'line',
        left: 0,
        top: 0,
        item: null,
        lineLabel: '',
        lineValue: null,
        dateLabel: ''
      }
    }
  },
  methods: {
    toTimeKey(dateStr) {
      const t = new Date(dateStr).getTime()
      return Number.isFinite(t) ? t : null
    },
    clearHistoricalChartHoverState() {
      this._historicalChartHoverKey = ''
      this.historicalChartTooltip.visible = false
      this.historicalChartTooltip.item = null
      this.historicalChartTooltip.lineLabel = ''
      this.historicalChartTooltip.lineValue = null
      this.historicalChartTooltip.dateLabel = ''
    },
    showHistoricalChartTooltip({
      hoverKey,
      mode,
      left,
      top,
      centerLeft = null,
      item = null,
      lineLabel = '',
      lineValue = null,
      dateLabel = ''
    }) {
      const isMobile = window.innerWidth <= 1024
      const isPhone = window.innerWidth <= 768
      const fallbackCenter = window.innerWidth / 2
      const nextLeft = isMobile ? centerLeft || fallbackCenter : left
      const adjustedTop = isPhone ? top - 20 : top
      const safeTop = Math.max(adjustedTop, isPhone ? 70 : 90)

      const sameHover =
        hoverKey === this._historicalChartHoverKey &&
        this.historicalChartTooltip.visible &&
        this.historicalChartTooltip.mode === mode

      if (sameHover) {
        const dx = Math.abs(nextLeft - this.historicalChartTooltip.left)
        const dy = Math.abs(safeTop - this.historicalChartTooltip.top)
        if (dx < 1 && dy < 1) return
      }

      this._historicalChartHoverKey = hoverKey
      this.historicalChartTooltip.visible = true
      this.historicalChartTooltip.mode = mode
      this.historicalChartTooltip.left = nextLeft
      this.historicalChartTooltip.top = safeTop
      this.historicalChartTooltip.item = item
      this.historicalChartTooltip.lineLabel = lineLabel
      this.historicalChartTooltip.lineValue = lineValue
      this.historicalChartTooltip.dateLabel = dateLabel
    },
    buildHistoricalChartOptions() {
      const vm = this
      return {
        ...HISTORICAL_CHART_BASE_OPTIONS,
        onHover(event, active) {
          handleHistoricalChartHover(vm, event, active)
        }
      }
    },
    applyHistoricalChartDatasetHoverDefaults(dataset) {
      const isActualRent = dataset.label === ACTUAL_RENT_LABEL
      return {
        ...dataset,
        hoverBorderWidth: 2,
        pointHoverRadius: isActualRent ? 6 : 5,
        pointHitRadius: isActualRent ? 14 : 10
      }
    }
  }
}
